import React, { useEffect, useMemo, useState } from "react";
import { Box, Badge, Tooltip } from "@mui/material";
import { Droppable } from "@hello-pangea/dnd";
import {
  PrecisionManufacturing as MachineIcon,
  ExpandMoreOutlined,
  ExpandLessOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { CustomerMachineryState, Machine } from "../../../machinery/models/models";
import SidebarMachineCategory from "./SidebarMachineCategory";
import SidebarMachineManufacturer from "./SidebarMachineManufacturer";
import SidebarMachineRow from "./SidebarMachineRow";

interface MachineSidebarProps {
  collapsed: boolean;
  sectionCollapsed: boolean;
  onToggleSectionCollapse: () => void;
}

/** Resolve the best available thumbnail URL for a machine */
const getMachineThumbnail = (machine: Machine): string | null => {
  return (
    machine.image?.content ||
    machine.catalogModel?.imageStandardUrl ||
    machine.catalogModel?.imageUrl ||
    null
  );
};

/** Group machines by manufacturer name */
const groupByManufacturer = (machines: Machine[]): Record<string, Machine[]> => {
  const result: Record<string, Machine[]> = {};
  machines.forEach((m) => {
    const key = m.manufacturer?.name || "Unknown";
    if (!result[key]) result[key] = [];
    result[key].push(m);
  });
  return result;
};

const MachineSidebar: React.FC<MachineSidebarProps> = ({
  collapsed,
  sectionCollapsed,
  onToggleSectionCollapse,
}) => {
  const { translate } = useTranslation("operations");
  const dispatch = useAppDispatch();

  const { machines: machinesByType, loading } = useAppSelector<CustomerMachineryState>(
    (state) => (state as any).customerMachinery
  );

  const [searchTerm, setSearchTerm] = useState("");

  // Fetch machines on mount if not already loaded
  useEffect(() => {
    const hasData = Object.keys(machinesByType).length > 0;
    if (!hasData && !loading) {
      dispatch(getMachines({}));
    }
  }, [dispatch, machinesByType, loading]);

  // Flatten all machines for counting and search
  const allMachines = useMemo(
    () => Object.values(machinesByType).flat(),
    [machinesByType]
  );

  // Filter by search term
  const filteredByType = useMemo(() => {
    if (!searchTerm.trim()) return machinesByType;

    const term = searchTerm.toLowerCase().trim();
    const result: Record<string, Machine[]> = {};

    Object.entries(machinesByType).forEach(([typeName, machines]) => {
      const filtered = machines.filter(
        (m) =>
          m.model?.toLowerCase().includes(term) ||
          m.manufacturer?.name?.toLowerCase().includes(term) ||
          m.customBrandName?.toLowerCase().includes(term) ||
          m.serialNumber?.toLowerCase().includes(term) ||
          m.internalNumber?.toLowerCase().includes(term)
      );
      if (filtered.length > 0) {
        result[typeName] = filtered;
      }
    });

    return result;
  }, [machinesByType, searchTerm]);

  const totalMachineCount = allMachines.length;

  // Machines with thumbnails for collapsed mini-preview
  const machinesWithThumbs = useMemo(
    () =>
      allMachines
        .filter((m) => getMachineThumbnail(m))
        .slice(0, 3),
    [allMachines]
  );

  // ---- Collapsed sidebar (48px) ----
  if (collapsed) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "8px 0",
          gap: "4px",
        }}
      >
        {/* Machine icon with count badge */}
        <Tooltip title={translate("machineSidebar.title")} placement="right">
          <Badge
            badgeContent={totalMachineCount}
            color="primary"
            max={999}
            sx={{
              "& .MuiBadge-badge": {
                fontFamily: dt.fontMono,
                fontSize: "9px",
                fontWeight: 700,
                minWidth: "16px",
                height: "16px",
                padding: "0 4px",
              },
            }}
          >
            <Box
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: dt.rowHover,
              }}
            >
              <MachineIcon sx={{ fontSize: 18, color: dt.textSecondary }} />
            </Box>
          </Badge>
        </Tooltip>

        {/* Mini thumbnails (up to 3) */}
        {machinesWithThumbs.map((m) => (
          <Box
            key={m.id}
            component="img"
            src={getMachineThumbnail(m) || ""}
            alt={m.model}
            sx={{
              width: "28px",
              height: "28px",
              borderRadius: "4px",
              objectFit: "cover",
            }}
          />
        ))}
      </Box>
    );
  }

  // ---- Section collapsed (within expanded sidebar) ----
  if (sectionCollapsed) {
    return (
      <Box
        onClick={onToggleSectionCollapse}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px", /* spec: section horizontal + vertical padding */
          cursor: "pointer",
          transition: "background-color 0.15s ease",
          flexShrink: 0,
          "&:hover": { backgroundColor: "#F7F5F0" /* spec: input-bg hover */ },
        }}
      >
        <Box
          sx={{
            fontFamily: dt.fontMono, /* spec: JetBrains Mono */
            fontSize: "11px",
            fontWeight: 600,
            color: "#8C9099", /* spec: text-muted */
            textTransform: "uppercase",
            letterSpacing: "0.1em", /* spec: section header */
          }}
        >
          {translate("machineSidebar.title")}
        </Box>
        <ExpandMoreOutlined sx={{ fontSize: 14, color: "#8C9099" /* spec: text-muted */, flexShrink: 0 }} />
      </Box>
    );
  }

  // ---- Expanded Machines section ----
  // Build a global draggable index counter across all machines
  let draggableIndex = 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      {/* Section header — spec: JetBrains Mono 11px/600 uppercase */}
      <Box
        onClick={onToggleSectionCollapse}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px", /* spec: section horizontal + vertical padding */
          flexShrink: 0,
          cursor: "pointer",
          "&:hover": { backgroundColor: "#F7F5F0" /* spec: input-bg hover */ },
          transition: "background-color 0.15s ease",
        }}
      >
        <Box
          sx={{
            fontFamily: dt.fontMono, /* spec: JetBrains Mono */
            fontSize: "11px",
            fontWeight: 600,
            color: "#8C9099", /* spec: text-muted */
            textTransform: "uppercase",
            letterSpacing: "0.1em", /* spec: section header */
          }}
        >
          {translate("machineSidebar.title")}
        </Box>
        <ExpandLessOutlined sx={{ fontSize: 14, color: "#8C9099" /* spec: text-muted */ }} />
      </Box>

      {/* Search input — spec: #F7F5F0 bg, 6px radius, 8px 12px padding */}
      <Box
        sx={{
          padding: "0 16px 8px", /* spec: section horizontal padding */
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 12px", /* spec: search input padding */
            borderRadius: "6px", /* spec: search input radius */
            border: "none",
            backgroundColor: "#F7F5F0", /* spec: input-bg */
            transition: "box-shadow 0.15s ease",
            "&:focus-within": { boxShadow: "0 0 0 1.5px #3B82F6" /* spec: status-scheduled for focus */ },
          }}
        >
          <SearchOutlined sx={{ fontSize: 12, color: "#8C9099" /* spec: text-muted */, flexShrink: 0 }} />
          <Box
            component="input"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            placeholder={translate("machineSidebar.search")}
            sx={{
              border: "none",
              outline: "none",
              backgroundColor: "transparent",
              fontFamily: dt.font,
              fontSize: "12px", /* spec: sidebar search placeholder */
              fontWeight: 400,
              color: "#0C1117", /* spec: text-primary */
              width: "100%",
              padding: 0,
              "&::placeholder": { color: "#8C9099" /* spec: text-muted */ },
            }}
          />
        </Box>
      </Box>

      {/* Machine tree wrapped in Droppable (source only) */}
      <Box sx={{ padding: "0 16px" /* spec: section horizontal padding */ }}>
        <Droppable droppableId="sidebar-machines" type="PERSON" isDropDisabled>
          {(provided) => (
            <Box ref={provided.innerRef} {...provided.droppableProps}>
              {Object.keys(filteredByType).length === 0 ? (
                <Box
                  sx={{
                    padding: "24px 0",
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: "#8C9099", /* spec: text-muted */
                    textAlign: "center",
                  }}
                >
                {translate("machineSidebar.noMachines")}
              </Box>
            ) : (
              Object.entries(filteredByType).map(([typeName, machines]) => {
                const manufacturerGroups = groupByManufacturer(machines);
                return (
                  <SidebarMachineCategory
                    key={typeName}
                    categoryName={typeName}
                    machineCount={machines.length}
                  >
                    {Object.entries(manufacturerGroups).map(([mfgName, mfgMachines]) => (
                      <SidebarMachineManufacturer
                        key={`${typeName}-${mfgName}`}
                        manufacturerName={mfgName}
                        machineCount={mfgMachines.length}
                      >
                        {mfgMachines.map((machine) => {
                          const idx = draggableIndex++;
                          return (
                            <SidebarMachineRow
                              key={machine.id}
                              machineId={machine.id}
                              modelName={machine.model}
                              serialNumber={machine.serialNumber}
                              thumbnailUrl={getMachineThumbnail(machine)}
                              draggableIndex={idx}
                              categoryKey={typeName}
                            />
                          );
                        })}
                      </SidebarMachineManufacturer>
                    ))}
                  </SidebarMachineCategory>
                );
              })
            )}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </Box>

      {/* Drag hint — spec: Sora 11px/400, #8C9099, centered */}
      <Box
        sx={{
          padding: "4px 16px",
          fontFamily: dt.font,
          fontSize: "11px",
          fontWeight: 400,
          color: "#8C9099", /* spec: text-muted */
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        {translate("machineSidebar.dragToAssign")}
      </Box>
    </Box>
  );
};

export default MachineSidebar;
